import { useState } from 'react';
import { 
  Lock, 
  AlertCircle, 
  Info, 
  Wrench, 
  Shield, 
  AlertTriangle,
  CheckCircle,
  X 
} from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_PUBLIC_BASE } from '../utils/supabase/info';
import { toast } from '../utils/toast';
import { sanitizeHtmlAllowSafe } from '../utils/sanitize-html';

interface AccessGateProps {
  onAccessGranted: () => void;
  customTitle?: string;
  customDescription?: string;
  customButtonText?: string;
  customIcon?: string;
  customTheme?: 'default' | 'warning' | 'danger' | 'info' | 'success';
  allowClose?: boolean;
  showButton?: boolean;
  showInfo?: boolean; // ✅ NEW: Toggle for info box
  customSubtitle?: string; // ✅ NEW: "Beta-tilgang påkrevd"
  customInfoText?: string; // ✅ NEW: Info box text
  customFooterText?: string; // ✅ NEW: Footer text (e.g. "Vil du bli pilot-bruker?")
  customContactEmail?: string; // ✅ NEW: Contact email
}

// Icon mapping
const iconMap: Record<string, any> = {
  Lock,
  AlertCircle,
  Wrench,
  Shield,
  AlertTriangle,
  CheckCircle,
  Info
};

// Theme configurations
const themeConfig = {
  default: {
    gradient: 'from-purple-50 via-pink-50 to-orange-50',
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    buttonGradient: 'from-purple-600 to-pink-600',
    borderColor: 'border-purple-100',
    infoBg: 'bg-purple-50',
    infoBorder: 'border-purple-100',
    infoText: 'text-purple-900',
    infoIcon: 'text-purple-600'
  },
  warning: {
    gradient: 'from-orange-50 via-yellow-50 to-amber-50',
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    buttonGradient: 'from-orange-600 to-amber-600',
    borderColor: 'border-orange-100',
    infoBg: 'bg-orange-50',
    infoBorder: 'border-orange-100',
    infoText: 'text-orange-900',
    infoIcon: 'text-orange-600'
  },
  danger: {
    gradient: 'from-red-50 via-rose-50 to-pink-50',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    buttonGradient: 'from-red-600 to-rose-600',
    borderColor: 'border-red-100',
    infoBg: 'bg-red-50',
    infoBorder: 'border-red-100',
    infoText: 'text-red-900',
    infoIcon: 'text-red-600'
  },
  info: {
    gradient: 'from-blue-50 via-cyan-50 to-sky-50',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    buttonGradient: 'from-blue-600 to-cyan-600',
    borderColor: 'border-blue-100',
    infoBg: 'bg-blue-50',
    infoBorder: 'border-blue-100',
    infoText: 'text-blue-900',
    infoIcon: 'text-blue-600'
  },
  success: {
    gradient: 'from-green-50 via-emerald-50 to-teal-50',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    buttonGradient: 'from-green-600 to-emerald-600',
    borderColor: 'border-green-100',
    infoBg: 'bg-green-50',
    infoBorder: 'border-green-100',
    infoText: 'text-green-900',
    infoIcon: 'text-green-600'
  }
};

export function AccessGate({ 
  onAccessGranted, 
  customTitle, 
  customDescription, 
  customButtonText,
  customIcon = 'Lock',
  customTheme = 'default',
  allowClose = true,
  showButton = true,
  showInfo = true, // ✅ NEW: Toggle for info box
  customSubtitle = 'Beta-tilgang påkrevd',
  customInfoText = '<strong>Pilot-fase:</strong> Talentfunn er for øyeblikket i en lukket beta-fase. Hvis du har fått tilgang til pilot-programmet, bruk tilgangskoden du mottok.',
  customFooterText = 'Vil du bli pilot-bruker?',
  customContactEmail = 'kontakt@talentfunn.no?subject=Pilot-program'
}: AccessGateProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const theme = themeConfig[customTheme];
  const IconComponent = iconMap[customIcon] || Lock;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const response = await fetch(`${API_PUBLIC_BASE}/access-gate/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: password }),
      });
      const data = response.ok ? await response.json() : null;
      if (response.ok && data?.ok) {
        localStorage.setItem('talentfunn_access_granted', 'true');
        localStorage.setItem('talentfunn_access_timestamp', new Date().toISOString());
        onAccessGranted();
      } else {
        setError(data?.error || 'Feil brukernavn eller passord');
        setPassword('');
      }
    } catch {
      setError('Kunne ikke sjekke tilgangskode. Prøv igjen.');
      setPassword('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (allowClose) {
      // Do NOT grant access when closing – user must enter valid code
      toast.error('Tast inn tilgangskode for å komme inn.');
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.gradient} flex items-center justify-center p-4`}>
      <div className="max-w-md w-full relative">
        {/* Close button (if allowed) */}
        {allowClose && (
          <button
            onClick={handleClose}
            className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
            aria-label="Lukk"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        )}

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="h-16 flex items-center justify-center mb-4">
            <svg 
              className="h-12" 
              viewBox="0 0 284.81 55.39" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#9333ea"/>
                  <stop offset="100%" stopColor="#ec4899"/>
                </linearGradient>
              </defs>
              <g>
                {/* Star icon */}
                <path fill="url(#gradient1)" d="M36.66,21.12l-10.1-1.91c-.87-.16-1.54-.83-1.7-1.7l-1.91-10.1c-.25-1.34-1.32-2.4-2.65-2.65-.87-.16-1.75.02-2.48.52-.73.5-1.22,1.25-1.39,2.12l-1.91,10.1c-.16.87-.83,1.54-1.7,1.7l-10.1,1.91c-1.34.25-2.4,1.32-2.65,2.65-.16.87.02,1.75.52,2.48s1.26,1.22,2.12,1.39l10.1,1.91c.87.16,1.54.83,1.7,1.7l1.91,10.1c.25,1.34,1.32,2.4,2.65,2.65.2.04.41.06.61.06,1.57,0,2.96-1.12,3.26-2.71l1.91-10.1c.16-.87.83-1.54,1.7-1.7l10.1-1.91c1.34-.25,2.4-1.31,2.65-2.65.34-1.8-.85-3.53-2.65-3.87ZM36.1,24.69l-10.1,1.91c-2.09.4-3.7,2-4.09,4.09l-1.91,10.11c-.03.17-.2.29-.37.25-.13-.02-.23-.13-.25-.26l-1.91-10.1c-.39-2.09-2-3.7-4.09-4.09l-10.11-1.91c-.11-.02-.17-.08-.2-.13-.03-.05-.07-.13-.05-.24.02-.13.13-.23.26-.25l10.1-1.91c2.09-.4,3.7-2,4.09-4.09l1.91-10.1s0,0,0,0c.02-.11.08-.17.13-.2.04-.03.1-.06.18-.06.02,0,.04,0,.06,0,.13.02.23.13.25.26l1.91,10.1c.4,2.09,2,3.7,4.09,4.09l10.11,1.91c.17.03.29.2.25.37-.02.13-.13.23-.26.26Z"/>
                <path fill="url(#gradient1)" d="M30.59,11.33h2.14v2.14c0,.83.67,1.5,1.5,1.5s1.5-.67,1.5-1.5v-2.14h2.14c.83,0,1.5-.67,1.5-1.5s-.67-1.5-1.5-1.5h-2.14v-2.14c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5,1.5v2.14h-2.14c-.83,0-1.5.67-1.5,1.5s.67,1.5,1.5,1.5Z"/>
                <circle fill="url(#gradient1)" cx="5.14" cy="38.92" r="5.14"/>
                {/* Text */}
                <text fill="url(#gradient1)" fontFamily="Roboto, Arial, sans-serif" fontSize="49" x="52" y="42">Talentfunn</text>
              </g>
            </svg>
          </div>
          <p className="text-gray-600">
            {customSubtitle}
          </p>
        </div>

        {/* Access form */}
        <div className={`bg-white rounded-2xl shadow-xl p-8 border ${theme.borderColor}`}>
          <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-100">
            <div className={`w-10 h-10 ${theme.iconBg} rounded-full flex items-center justify-center`}>
              <IconComponent className={`w-5 h-5 ${theme.iconColor}`} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {customTitle || 'Pilot-fase'}
              </h2>
              <p className="text-sm text-gray-600">
                {customDescription || 'Skriv inn tilgangskode'}
              </p>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">{error}</p>
                <p className="text-xs text-red-700 mt-1">
                  Kontakt administratoren for tilgang
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Brukernavn
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                placeholder="Skriv inn brukernavn"
                required
                autoFocus
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Passord
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Skriv inn passordet"
                required
              />
            </div>

            {showButton && (
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full bg-gradient-to-r ${theme.buttonGradient} text-white py-3 px-6 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader size="md" className="text-white" />
                    Sjekker...
                  </span>
                ) : (
                  customButtonText || 'Få tilgang'
                )}
              </button>
            )}
          </form>

          {/* Info box */}
          {showInfo && (
            <div className={`mt-6 p-4 ${theme.infoBg} rounded-lg border ${theme.infoBorder}`}>
              <div className="flex items-start gap-3">
                <Info className={`w-5 h-5 ${theme.infoIcon} flex-shrink-0 mt-0.5`} />
                <p className={`text-xs ${theme.infoText} leading-relaxed`} dangerouslySetInnerHTML={{ __html: sanitizeHtmlAllowSafe(customInfoText) }} />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-600">
            {customFooterText}{' '}
            <a 
              href={`mailto:${customContactEmail}`} 
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              Kontakt oss
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
